// src/data/sizeGuide.ts
// Size-guide data for the store's apparel. Our Printful blanks (e.g. Comfort
// Colors 1717) are UNISEX / retail adult fit, so the table is body-measurement
// based (anyone can measure and choose, regardless of gender) and paired with
// explicit women's-fit guidance — unisex tees run relaxed and boxy on a
// feminine frame, which is the #1 sizing question we get.
//
// Measurements are approximate for a standard unisex heavyweight tee; verify
// against the exact blank if you change suppliers. Kept here (not hard-coded in
// the component) so the numbers are easy to adjust.

export interface SizeRow {
  size: string;
  /** Body chest circumference the size is cut to fit, in inches. */
  chestToFitIn: string;
  /** Finished garment body length (shoulder to hem), in inches. */
  lengthIn: string;
  /** Rough US women's dress size for a RELAXED (true unisex) fit. */
  womensRelaxed: string;
}

export const UNISEX_TEE_SIZES: SizeRow[] = [
  { size: "XS", chestToFitIn: "31–34", lengthIn: "27", womensRelaxed: "0–2" },
  { size: "S", chestToFitIn: "34–37", lengthIn: "28", womensRelaxed: "4–6" },
  { size: "M", chestToFitIn: "38–41", lengthIn: "29", womensRelaxed: "8–10" },
  { size: "L", chestToFitIn: "42–45", lengthIn: "30", womensRelaxed: "12–14" },
  { size: "XL", chestToFitIn: "46–49", lengthIn: "31", womensRelaxed: "16–18" },
  {
    size: "2XL",
    chestToFitIn: "50–53",
    lengthIn: "32",
    womensRelaxed: "20–22",
  },
  {
    size: "3XL",
    chestToFitIn: "54–57",
    lengthIn: "33",
    womensRelaxed: "24–26",
  },
];

// ── Women's cropped fit ──────────────────────────────────────────────────────
// The crop top is NOT the unisex blank: it's AS Colour 4062 (Printful catalog
// product 636), a women's cut with a deliberately short body. Showing it the
// unisex table would be actively wrong — that table's advice is "size DOWN for
// a fitted look", which is exactly backwards on a garment that's already
// women's-cut.
//
// Numbers are Printful's supplier-provided "measure yourself" chart for product
// 636 (GET /v2/catalog-products/636/sizes?unit=inches), rounded to the nearest
// half inch. Printful notes measurements can vary up to 2".
export interface CropSizeRow {
  size: string;
  /** Body chest circumference the size is cut to fit, in inches. */
  chestToFitIn: string;
  /** Finished garment body length (shoulder to cropped hem), in inches. */
  lengthIn: string;
}

export const WOMENS_CROP_SIZES: CropSizeRow[] = [
  { size: "XS", chestToFitIn: "36–37½", lengthIn: "17¼" },
  { size: "S", chestToFitIn: "38–39½", lengthIn: "17¾" },
  { size: "M", chestToFitIn: "40–41½", lengthIn: "18¼" },
  { size: "L", chestToFitIn: "42–43½", lengthIn: "18¾" },
  { size: "XL", chestToFitIn: "44–45½", lengthIn: "19" },
];

export const CROP_FIT_GUIDANCE = {
  heading: "About this cropped fit",
  points: [
    "This is a women's cut, not our unisex tee — so order your usual size.",
    "The body is cropped on purpose: the length shown is measured from the shoulder to the cropped hem, so it sits above the waist.",
    "Want it looser or longer? Size up — the length grows with the size.",
    "Between sizes? Size up for more coverage, down for a closer fit.",
  ],
};

/**
 * Which size chart a product should show. Product names come from the Square
 * catalog, so this is name-based on purpose — there's no per-product fit field
 * in Square to hang it off, and the catalog is small enough that a name test is
 * honest about what it is. Anything unmatched falls back to the unisex chart.
 */
export type Fit = "unisex" | "womensCrop";

export function fitForProductName(name?: string | null): Fit {
  return /crop/i.test(name ?? "") ? "womensCrop" : "unisex";
}

// Shown prominently — the guidance that actually resolves the unisex-fit
// confusion for women.
export const WOMENS_FIT_GUIDANCE = {
  heading: "Buying for a women's fit?",
  points: [
    "Our shirts are a unisex cut, which is roomier and boxier than a women's-cut tee.",
    "For a fitted, tailored look, size DOWN one from your usual women's size.",
    "For the classic relaxed / oversized look, order your usual size.",
    "Between sizes? Size down for a closer fit, up for extra room.",
  ],
};

// Simple, unambiguous measuring instructions so a customer can match the chart.
export const HOW_TO_MEASURE = [
  {
    label: "Chest",
    text: "Measure around the fullest part of your chest, keeping the tape level under your arms.",
  },
  {
    label: "Length",
    text: "Compare against a shirt you already own — measure from the highest point of the shoulder straight down to the hem.",
  },
  {
    label: "Best trick",
    text: "Lay a favorite shirt flat and compare its measurements to the chart for the closest match.",
  },
];

export const SIZE_GUIDE_FOOTNOTE =
  "Measurements are approximate and can vary up to an inch — every item is made to order, so please choose carefully (we can't accept exchanges for size).";
